/**
 * format.js — one way to write a figure
 * =====================================
 * Usability review of 23 October 2026, item 6: *"The app mixes $1,212,058.50,
 * 1.2M, "(34,680.41)" and "-$34,680.41". Use one style, with negatives in
 * parentheses ... Cents on fund-level figures are noise; whole dollars are
 * enough there."*
 *
 * So every page that shows money reads it from here:
 *
 *   money(-34680.41)                    ($34,680.41)
 *   money(1212058.5, { whole: true })   $1,212,059       fund-level figures
 *   money(5000, { currency: "SEK" })    SEK 5,000.00     never a dollar sign
 *   compactMoney(-1250000)              ($1.3M)          chart axes only
 *   pct(-0.4567)                        (0.5%)           a percentage, not a ratio
 *
 * A missing figure is a dash, never 0.00: a blank is not a balance of nothing
 * (CLAUDE.md §0.30).
 */

export const toNumber = (value) =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? null
    : Number(value);

export const DASH = "—";

const grouped = (n, decimals) =>
  Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const wrapNegative = (n, text) => (n < 0 ? `(${text})` : text);

// A rounded zero is zero: -0.004 at two decimals must not print as "(0.00)".
const roundsToZero = (n, decimals) => Math.abs(n) < 0.5 * 10 ** -decimals;

/** A money amount. Dollars carry "$"; any other currency carries its code. */
export function money(value, { currency = "USD", decimals, whole = false, symbol = true } = {}) {
  const n = toNumber(value);
  if (n === null) return DASH;
  const places = decimals ?? (whole ? 0 : 2);
  const shown = roundsToZero(n, places) ? 0 : n;
  const code = (currency || "USD").toUpperCase();
  const body = grouped(shown, places);
  const text = !symbol ? body : code === "USD" ? `$${body}` : `${code} ${body}`;
  return wrapNegative(shown, text);
}

/** A plain number in the same style: thousands grouped, negatives bracketed. */
export function amount(value, decimals = 2) {
  const n = toNumber(value);
  if (n === null) return DASH;
  const shown = roundsToZero(n, decimals) ? 0 : n;
  return wrapNegative(shown, grouped(shown, decimals));
}

/** A quantity of shares or units: whole when it is whole. */
export function quantity(value) {
  const n = toNumber(value);
  if (n === null) return DASH;
  return amount(n, Number.isInteger(n) ? 0 : 2);
}

/** 1.25M, 640K — for a chart axis or a tooltip where space is the point. */
export function compactMoney(value, { currency = "USD" } = {}) {
  const n = toNumber(value);
  if (n === null) return DASH;
  const a = Math.abs(n);
  const [scaled, unit] = a >= 1e9 ? [a / 1e9, "B"] : a >= 1e6 ? [a / 1e6, "M"] : a >= 1e3 ? [a / 1e3, "K"] : [a, ""];
  const digits = unit && scaled < 100 ? 1 : 0;
  const body = `${scaled.toFixed(digits)}${unit}`;
  const code = (currency || "USD").toUpperCase();
  const text = code === "USD" ? `$${body}` : `${code} ${body}`;
  return n < 0 && !roundsToZero(n, digits) ? `(${text})` : text;
}

/** A percentage already expressed in percent (12.5 means 12.5%). */
export function pct(value, digits = 1) {
  const n = toNumber(value);
  if (n === null) return DASH;
  const shown = roundsToZero(n, digits) ? 0 : n;
  return wrapNegative(shown, `${grouped(shown, digits)}%`);
}

/** A multiple: 1.25x. Never negative in practice, never bracketed. */
export function multiple(value, digits = 2) {
  const n = toNumber(value);
  return n === null ? DASH : `${n.toFixed(digits)}x`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 23 Oct 2026 — a date as a person reads it, from an ISO date. */
export function day(value) {
  if (!value) return DASH;
  const d = new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return String(value);
  // Spelled out here rather than through a locale: en-GB writes "Sept" in
  // current browsers and en-US puts the month first.
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "2 min ago", "10:15" — how long ago something was read. */
export function ago(timestamp, now = Date.now()) {
  if (!timestamp) return DASH;
  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const d = new Date(timestamp);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return now - timestamp < 86400000 ? time : `${day(d.toISOString())} ${time}`;
}

/** The tone a signed figure earns: a colour for gain, loss or nil. */
export function tone(value) {
  const n = toNumber(value);
  if (n === null || n === 0) return "neutral";
  return n > 0 ? "positive" : "negative";
}
